/** Titles stripped from the start of a display name. */
export const NAME_HONORIFICS = new Set([
  'mr',
  'mr.',
  'mrs',
  'mrs.',
  'ms',
  'ms.',
  'dr',
  'dr.',
  'prof',
  'prof.',
]);

/**
 * Single-letter initials such as `M.` or `K`.
 * @param {string} token
 */
export function isNameInitialToken(token) {
  return /^[A-Za-z]\.?$/.test(String(token ?? '').trim());
}

/**
 * @param {string} token
 */
export function isNameHonorific(token) {
  return NAME_HONORIFICS.has(String(token ?? '').trim().toLowerCase());
}

/**
 * @param {string} fullName
 * @returns {string[]}
 */
export function tokenizeDisplayName(fullName) {
  return String(fullName ?? '').trim().split(/\s+/).filter(Boolean);
}

/**
 * @param {string[]} tokens
 * @returns {string[]}
 */
export function stripNameHonorifics(tokens) {
  let index = 0;
  while (index < tokens.length && isNameHonorific(tokens[index])) {
    index += 1;
  }
  return tokens.slice(index);
}

/**
 * @param {string[]} tokens
 * @returns {string[]}
 */
export function stripLeadingNameInitials(tokens) {
  let index = 0;
  while (index < tokens.length && isNameInitialToken(tokens[index])) {
    index += 1;
  }
  return tokens.slice(index);
}

/**
 * When only two name parts remain after initials, a long final token is often the
 * given name following a shorter patronymic or middle name (e.g. Naga Sriharsha).
 */
const SUBSTANTIAL_GIVEN_NAME_MIN_LENGTH = 8;

/**
 * @param {string[]} tokens
 * @returns {string[]}
 */
export function pickFriendlyNameTokens(tokens) {
  if (tokens.length <= 1) return tokens;

  const last = tokens[tokens.length - 1];
  if (tokens.length === 2 && last.length >= SUBSTANTIAL_GIVEN_NAME_MIN_LENGTH) {
    return [last];
  }

  return tokens;
}

/**
 * Full legal-style name with titles removed.
 * @param {string} fullName
 * @returns {string}
 */
export function getFullNameWithoutTitle(fullName) {
  const tokens = stripNameHonorifics(tokenizeDisplayName(fullName));
  return tokens.join(' ') || String(fullName ?? '').trim();
}

/**
 * Friendly name for greetings and profile summaries.
 *
 * Priority:
 * 1. preferredName when provided
 * 2. Name without titles/initials, using the last substantial given-name token when applicable
 * 3. Full name without title
 *
 * @param {{ fullName?: string, preferredName?: string | null }} params
 * @returns {string}
 */
export function getFriendlyDisplayName({ fullName = '', preferredName = null } = {}) {
  const preferred = String(preferredName ?? '').trim();
  if (preferred) return preferred;

  const raw = String(fullName ?? '').trim();
  const tokens = tokenizeDisplayName(raw);
  if (tokens.length === 0) return 'there';

  const withoutHonorifics = stripNameHonorifics(tokens);
  const withoutLeadingInitials = stripLeadingNameInitials(withoutHonorifics);
  const friendlyTokens = pickFriendlyNameTokens(withoutLeadingInitials);

  if (friendlyTokens.length > 0) {
    return friendlyTokens.join(' ');
  }

  if (withoutHonorifics.length > 0) {
    return withoutHonorifics.join(' ');
  }

  return raw;
}

/**
 * Avatar initials derived from the friendly display name.
 * @param {{ fullName?: string, preferredName?: string | null }} params
 * @returns {string}
 */
export function getDisplayInitials({ fullName = '', preferredName = null } = {}) {
  const friendly = getFriendlyDisplayName({ fullName, preferredName });
  const parts = tokenizeDisplayName(friendly);

  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** @deprecated Use getFriendlyDisplayName instead. */
export function firstNameFrom(fullName) {
  return getFriendlyDisplayName({ fullName });
}
